/**
 * Upsell FBT widget — fetches cross-store "Frequently bought together"
 * recommendations from the transport-portal endpoint and renders them
 * client-side. Independent of Shopify's native recommendations pipeline.
 *
 * Render contract (from the design review):
 *  - loading  → skeleton tiles, reserve height (no CLS), no event
 *  - error    → render nothing, fail closed (never block the PDP)
 *  - empty    → render nothing, collapse fully
 *  - < 3 recs → render nothing (an FBT row of 1-2 items reads as broken)
 *  - ok       → render the row
 *
 * Tracking events are deliberately NOT fired here (Phase 4).
 */
class UpsellFbt extends HTMLElement {
  connectedCallback() {
    this.endpoint = this.dataset.endpoint;
    this.productId = this.dataset.productId;
    this.limit = parseInt(this.dataset.limit || '8', 10);
    this.minItems = parseInt(this.dataset.minItems || '3', 10);
    this.list = this.querySelector('[data-upsell-list]');
    this.root = this.querySelector('[data-upsell-root]');
    this.viewport = this.querySelector('[data-upsell-viewport]');
    this.prevBtn = this.querySelector('[data-upsell-prev]');
    this.nextBtn = this.querySelector('[data-upsell-next]');

    if (!this.endpoint || !this.productId || !this.list) {
      this.#hide();
      return;
    }
    this.#load();
  }

  async #load() {
    this.#showSkeleton();
    let data;
    try {
      const url = `${this.endpoint}?product_id=${encodeURIComponent(this.productId)}&limit=${this.limit}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) {
        this.#hide();
        return;
      }
      data = await res.json();
    } catch {
      this.#hide(); // fail closed — never block the product page
      return;
    }

    const recs = (data && data.recommendations) || [];
    if (recs.length < this.minItems) {
      this.#hide(); // empty or too few to honestly call "frequently bought together"
      return;
    }
    this.#render(recs);
  }

  #showSkeleton() {
    const tiles = Array.from({ length: Math.min(this.limit, 5) }, () =>
      `<li class="upsell-fbt__tile upsell-fbt__tile--skeleton" aria-hidden="true">
         <div class="upsell-fbt__link">
           <div class="upsell-fbt__img-skeleton"></div>
           <div class="upsell-fbt__line-skeleton"></div>
           <div class="upsell-fbt__line-skeleton upsell-fbt__line-skeleton--short"></div>
         </div>
       </li>`
    ).join('');
    this.list.innerHTML = tiles;
    if (this.root) this.root.hidden = false;
  }

  #render(recs) {
    const money = (price) => {
      if (price == null || price === '') return '';
      const n = Number(price);
      if (Number.isNaN(n)) return '';
      // EUR, French formatting (matches the storefront locale).
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
    };

    this.list.innerHTML = recs
      .map((r) => {
        const img = r.imageUrl
          ? `<img class="upsell-fbt__img" data-upsell-img src="${this.#esc(r.imageUrl)}" alt="" loading="lazy" width="160" height="160">`
          : `<div class="upsell-fbt__img upsell-fbt__img--placeholder" aria-hidden="true"></div>`;
        // Dual price like the store's native cards: prominent TTC, dimmed HT below.
        // Falls back to a single HT price when no TTC (no vat_rate) is available.
        const ht = money(r.price);
        const ttc = money(r.priceTtc);
        let priceBlock = '';
        if (ttc) {
          priceBlock = `<div class="upsell-fbt__price">
            <span class="upsell-fbt__price-ttc">${ttc}<span class="upsell-fbt__tax">TTC</span></span>
            ${ht ? `<span class="upsell-fbt__price-ht">${ht}<span class="upsell-fbt__tax">HT</span></span>` : ''}
          </div>`;
        } else if (ht) {
          priceBlock = `<div class="upsell-fbt__price"><span class="upsell-fbt__price-ttc">${ht}<span class="upsell-fbt__tax">HT</span></span></div>`;
        }
        const href = `/products/${this.#esc(r.handle)}`;
        return `<li class="upsell-fbt__tile">
          <a class="upsell-fbt__link" href="${href}" aria-label="Voir ${this.#esc(r.title)}">
            ${img}
            <span class="upsell-fbt__title">${this.#esc(r.title)}</span>
            ${priceBlock}
          </a>
        </li>`;
      })
      .join('');
    if (this.root) this.root.hidden = false;
    this.#guardImages();
    this.#setupCarousel();
    // Cross-block dedup: register the ids we displayed so the category block
    // (which loads after, below us) can exclude them. Additive only — does not
    // change FBT behavior. See assets/upsell-category.js for the contract.
    window.__upsellDisplayed ??= new Set();
    recs.forEach((r) => window.__upsellDisplayed.add(String(r.productId)));
  }

  // If a product image fails to load (e.g. the CDN URL went stale after a Shopify
  // image reprocessing), swap it for the neutral placeholder instead of leaving a
  // broken-image icon. CSP-safe: bound in JS, no inline onerror.
  #guardImages() {
    this.list.querySelectorAll('[data-upsell-img]').forEach((img) => {
      const swap = () => img.classList.add('upsell-fbt__img--placeholder');
      if (img.complete && img.naturalWidth === 0) swap();
      else img.addEventListener('error', swap, { once: true });
    });
  }

  #setupCarousel() {
    const vp = this.viewport;
    if (!vp || !this.prevBtn || !this.nextBtn) return;

    // Scroll by roughly one card width per click.
    const step = () => {
      const tile = this.list.querySelector('.upsell-fbt__tile');
      const gap = 20;
      return tile ? tile.getBoundingClientRect().width + gap : vp.clientWidth * 0.8;
    };
    this.prevBtn.addEventListener('click', () => vp.scrollBy({ left: -step() * 2, behavior: 'smooth' }));
    this.nextBtn.addEventListener('click', () => vp.scrollBy({ left: step() * 2, behavior: 'smooth' }));

    const update = () => {
      const overflowing = vp.scrollWidth - vp.clientWidth > 4;
      // Arrows only exist when there's something to scroll to.
      this.prevBtn.hidden = !overflowing;
      this.nextBtn.hidden = !overflowing;
      if (!overflowing) return;
      const atStart = vp.scrollLeft <= 2;
      const atEnd = vp.scrollLeft >= vp.scrollWidth - vp.clientWidth - 2;
      this.prevBtn.disabled = atStart;
      this.nextBtn.disabled = atEnd;
    };
    vp.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    // Images change layout width once loaded; recompute then.
    this.list.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', update, { once: true });
    });
    update();
  }

  #hide() {
    // Off / empty / error all collapse to the same "nothing rendered" output.
    if (this.root) this.root.hidden = true;
    this.list.innerHTML = '';
  }

  #esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('upsell-fbt')) {
  customElements.define('upsell-fbt', UpsellFbt);
}
